import React, { useMemo, useRef, useEffect } from 'react';
import { layer } from 'react-openlayers';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { LocalAtRecord } from '../../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { HeatmapAttribute, HeatmapAttributeCalculator, HeatmapDataPoint } from './domain/HeatmapAttribute';

export type { HeatmapAttribute };

interface Props {
  jobs: LocalAtRecord<JobsFieldSet>[];
  attribute: HeatmapAttribute;
  getPullinFromJob: (job: LocalAtRecord<JobsFieldSet>) => [number, number] | undefined;
  blur?: number;
  radius?: number;
  gradient?: string[];
}

/**
 * Functional React component for rendering a heatmap layer on the map
 *
 * This component visualizes job density and attributes using OpenLayers Heatmap layer.
 * It follows Domain-Driven Design principles by using domain services for calculations.
 */
export function HeatStatsLayer({
  jobs,
  attribute,
  getPullinFromJob,
  blur = 30,
  radius = 25,
  gradient = ['#00f', '#0ff', '#0f0', '#ff0', '#f00'], // Default gradient from blue to red
}: Props) {
  // Keep the source stable across renders to avoid WebGL context loss
  const sourceRef = useRef<VectorSource<Point>>(new VectorSource<Point>());

  // Create features from jobs using domain logic
  const features = useMemo(() => {
    if (!jobs || jobs.length === 0) {
      return [];
    }

    const dataPoints = HeatmapAttributeCalculator.createDataPoints(jobs, attribute, getPullinFromJob);

    const createdFeatures = dataPoints.map((point: HeatmapDataPoint) => {
      // Note: point.coordinates are already in map projection from getPullinFromJob
      // which calls convertProjection4329, so we don't need to call fromLonLat
      const feature = new Feature<Point>({
        geometry: new Point(point.coordinates),
      });

      // Store weight and metadata in feature properties
      feature.set('weight', point.weight);
      feature.set('jobId', point.jobId);
      feature.set('metadata', point.metadata);

      return feature;
    });

    return createdFeatures;
  }, [jobs, attribute, getPullinFromJob]);

  // Update source features when they change
  useEffect(() => {
    const source = sourceRef.current;
    source.clear();

    if (features.length > 0) {
      source.addFeatures(features);
      // Notify the source that it has changed so the layer re-renders
      source.changed();
    }
  }, [features]);

  // Weight function to extract weight from features
  const weightFunction = (feature: any): number => {
    return feature.get('weight') || 0;
  };

  // Render HeatmapReact with stable source and proper props
  // React will handle the lifecycle - only one layer will exist per component instance
  return (
    <layer.Heatmap
      source={sourceRef.current as any}
      blur={blur}
      radius={radius}
      gradient={gradient || undefined}
      weight={weightFunction}
      opacity={0.8}
      zIndex={1000}
    />
  );
}
